import { isAdmin } from '@/constants/roles';
import { isEmbeddedSidebar } from '@/lib/embedded-sidebar';
import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';

import { useAuth, useConfig } from '@chainlit/react-client';

import LeftSidebar from '@/components/LeftSidebar';
import DMRightSideBar from '@/components/RightSidebar/DM/DMRightSideBar';
import type { SidebarPanelType } from '@/components/RightSidebar/DM/types';
import { Header } from '@/components/header';
import {
    SidebarInset,
    SidebarProvider,
    useSidebar
} from '@/components/ui/sidebar';

import { useEmbeddedRouteSync } from '@/hooks/useEmbeddedRouteSync';
import { useIsBasicUser } from '@/hooks/useIsBasicUser';
import { useIsChatPage } from '@/hooks/useIsChatPage';

import { rightSidebarPanelState } from '@/state/project';
import { userEnvState } from 'state/user';

const isRightSidebarOpen = (panelState: SidebarPanelType): boolean =>
    panelState !== null;

// Routes with their own full-page layout and back-nav (e.g. the admin
// panel, the scheduled-jobs section) — they get none of the chat shell's
// header bar or Files/Charts/Apps right sidebar around them, not just a
// hidden one. `useLocation().pathname` is already basename-relative, so
// these compare directly with no basename handling needed.
const NO_CHROME_ROUTES = [
    '/admin',
    '/scheduled-jobs',
    '/artifacts',
    '/organization-artifacts',
    '/organization-threads'
];

const BASIC_USER_ROUTES = [
    '/organization-artifacts',
    '/scheduled-jobs/bank',
    '/scheduled-jobs/stream',
    '/scheduled-jobs/article'
];

const mayBasicUserVisit = (pathname: string, userIsAdmin: boolean) =>
    pathname === '/scheduled-jobs' ||
    (userIsAdmin &&
        (pathname === '/admin' || pathname.startsWith('/admin/'))) ||
    BASIC_USER_ROUTES.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`)
    );

const isNoChromeRoute = (pathname: string) =>
    NO_CHROME_ROUTES.some(
        (route) => pathname === route || pathname.startsWith(`${route}/`)
    );

const useLeftSidebarAutoToggle = () => {
    const rightSidebarPanel = useRecoilValue(rightSidebarPanelState);
    const { setOpen: setLeftSidebarOpen, isMobile } = useSidebar();
    const previousRightSidebarState = useRef(rightSidebarPanel);

    useEffect(() => {
        if (isMobile) {
            return;
        }

        const wasRightSidebarOpen = isRightSidebarOpen(
            previousRightSidebarState.current
        );
        const isRightSidebarNowOpen = isRightSidebarOpen(rightSidebarPanel);
        const hasRightSidebarOpened =
            !wasRightSidebarOpen && isRightSidebarNowOpen;
        const hasRightSidebarClosed =
            wasRightSidebarOpen && !isRightSidebarNowOpen;

        if (hasRightSidebarOpened) {
            setLeftSidebarOpen(false);
        }

        if (hasRightSidebarClosed) {
            setLeftSidebarOpen(true);
        }

        previousRightSidebarState.current = rightSidebarPanel;
    }, [rightSidebarPanel, setLeftSidebarOpen, isMobile]);
};

const MainContent = ({ noChrome }: { noChrome: boolean }) => (
    <div className="flex flex-col w-full h-full">
        {!noChrome && <Header />}
        <div className="[&::-webkit-scrollbar]:hidden relative flex flex-row grow w-full min-h-0 [-ms-overflow-style:none] overflow-auto scrollbar-none">
            {/* <div className="top-0 z-10 absolute inset-x-0 bg-linear-to-b from-background to-transparent h-6 pointer-events-none" /> */}
            <Outlet />
        </div>
    </div>
);

const PageWithSidebars = ({ noChrome }: { noChrome: boolean }) => {
    const isChatPage = useIsChatPage();

    return (
        <>
            <LeftSidebar />
            <SidebarInset className="max-h-svh">
                <MainContent noChrome={noChrome} />
            </SidebarInset>
            {isChatPage && !noChrome && <DMRightSideBar />}
        </>
    );
};

const PageWithoutSidebars = ({ noChrome }: { noChrome: boolean }) => {
    const isChatPage = useIsChatPage();

    return (
        <div
            className={`flex flex-row ${
                isEmbeddedSidebar ? 'w-full h-dvh' : 'w-screen h-screen'
            }`}
        >
            <div className="flex-1 min-w-0">
                <MainContent noChrome={noChrome} />
            </div>
            {!isEmbeddedSidebar && isChatPage && !noChrome && (
                <DMRightSideBar />
            )}
        </div>
    );
};

// Embedded (report companion / report sidebar): no left sidebar — the thread
// history and its controls are not available inside a report, only the chat.
const EmbeddedPage = ({ noChrome }: { noChrome: boolean }) => (
    <div className="flex flex-row w-full h-dvh">
        <div className="flex-1 min-w-0">
            <MainContent noChrome={noChrome} />
        </div>
    </div>
);

const PageContent = ({
    historyEnabled,
    noChrome
}: {
    historyEnabled: boolean;
    noChrome: boolean;
}) => {
    useLeftSidebarAutoToggle();

    if (isEmbeddedSidebar) return <EmbeddedPage noChrome={noChrome} />;
    return (
        <>
            {historyEnabled ? (
                <PageWithSidebars noChrome={noChrome} />
            ) : (
                <PageWithoutSidebars noChrome={noChrome} />
            )}
        </>
    );
};

const Page = () => {
    const { config } = useConfig();
    const { data, user } = useAuth();
    const userEnv = useRecoilValue(userEnvState);
    const { pathname } = useLocation();
    const noChrome = isNoChromeRoute(pathname);

    // Mirror the chat route to the embedding host (BIG) so its address bar
    // tracks the open thread and a reload restores it.
    useEmbeddedRouteSync();

    // No-chrome routes (e.g. /admin, /scheduled-jobs) render their own
    // full-page layout and scroll entirely within their own internal panes —
    // block the document's own scroll while on one of them so the browser's
    // native scrollbar never shows up here, rather than leaving the document
    // to catch whatever overflow escapes an inner container. Scoped to just
    // these routes (toggled on mount/route-change, restored on cleanup)
    // instead of disabling document scroll globally.
    useEffect(() => {
        if (!noChrome) return;
        document.body.classList.add('overflow-hidden');
        return () => document.body.classList.remove('overflow-hidden');
    }, [noChrome]);

    const isBasicUser = useIsBasicUser();
    const missingEnvKey = config?.userEnv?.find((key) => !userEnv[key]);
    const hasHistoryEnabled =
        config?.dataPersistence && (data as any)?.requireLogin;

    if (missingEnvKey) {
        return <Navigate to="/env" />;
    }

    if (
        isBasicUser &&
        !isEmbeddedSidebar &&
        !mayBasicUserVisit(
            pathname,
            isAdmin(
                (
                    user as
                        | { metadata?: { organization_roles?: string[] } }
                        | null
                        | undefined
                )?.metadata?.organization_roles
            )
        )
    ) {
        if (!config) return null;
        const basicHome =
            config.features?.organization_artifacts_enabled === true
                ? '/organization-artifacts'
                : config.features?.scheduled_jobs_enabled === true
                ? '/scheduled-jobs'
                : null;
        return basicHome ? (
            <Navigate to={basicHome} replace />
        ) : (
            <div className="flex justify-center items-center p-8 w-full h-full text-muted-foreground text-sm">
                You do not have access to anything yet. Ask an admin for access.
            </div>
        );
    }

    return (
        <SidebarProvider defaultOpen={true}>
            <PageContent
                historyEnabled={hasHistoryEnabled}
                noChrome={noChrome}
            />
        </SidebarProvider>
    );
};

export default Page;
